import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	MqttReadingRMItem,
	MqttSourceRM,
	MySourcesParams,
	ResellerDetail,
	ResellerListItem,
	ResellerManagerProfile,
	ResellersParams,
	RmDashboard,
	RmDashboardParams,
	RmMqttReadingsParams,
} from "./types";

export function useRmDashboard(
	params: RmDashboardParams = {},
): UseQueryResult<RmDashboard, Error> {
	return useQuery({
		queryKey: queryKeys.resellerManager.dashboard(params),
		queryFn: async (): Promise<RmDashboard> => {
			const res = await api.get<RmDashboard>(
				"/api/reseller/manager/dashboard/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useCreatedResellers(
	params: ResellersParams = {},
): UseQueryResult<PaginatedResponse<ResellerListItem>, Error> {
	return useQuery({
		queryKey: queryKeys.resellerManager.resellers(params),
		queryFn: async (): Promise<PaginatedResponse<ResellerListItem>> => {
			const res = await api.get<
				PaginatedResponse<ResellerListItem> | ResellerListItem[]
			>("/api/reseller/manager/created_resellers/", { params });
			if (Array.isArray(res.data)) {
				return {
					count: res.data.length,
					next: null,
					previous: null,
					last: null,
					first: null,
					results: res.data,
				};
			}
			return res.data;
		},
	});
}

export function useReseller(id: number): UseQueryResult<ResellerDetail, Error> {
	return useQuery({
		queryKey: queryKeys.resellerManager.reseller(id),
		queryFn: async (): Promise<ResellerDetail> => {
			const res = await api.get<ResellerDetail>(`/api/reseller/manager/${id}/`);
			return res.data;
		},
		enabled: !!id,
	});
}

export function useRmMySources(
	params: MySourcesParams = {},
): UseQueryResult<PaginatedResponse<MqttSourceRM>, Error> {
	return useQuery({
		queryKey: queryKeys.resellerManager.mySources(params),
		queryFn: async (): Promise<PaginatedResponse<MqttSourceRM>> => {
			const res = await api.get<PaginatedResponse<MqttSourceRM>>(
				"/api/reseller/manager/my_sources/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useRmMqttReadings(
	params: RmMqttReadingsParams = {},
): UseQueryResult<PaginatedResponse<MqttReadingRMItem>, Error> {
	return useQuery({
		queryKey: queryKeys.resellerManager.mqttReadings(params),
		queryFn: async (): Promise<PaginatedResponse<MqttReadingRMItem>> => {
			const res = await api.get<PaginatedResponse<MqttReadingRMItem>>(
				"/api/reseller/readings/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useRmMyProfile(): UseQueryResult<
	ResellerManagerProfile,
	Error
> {
	return useQuery({
		queryKey: queryKeys.resellerManager.myProfile(),
		queryFn: async (): Promise<ResellerManagerProfile> => {
			const res = await api.get<ResellerManagerProfile>(
				"/api/reseller/manager/my_profile/",
			);
			return res.data;
		},
	});
}
